// [sc-49210] Elementi per pagina delle liste calcolati dallo schermo: tante
// righe quante ne stanno senza scroll verticale della pagina.

export const DEFAULT_PAGE_SIZE = 6;
export const MIN_PAGE_SIZE = 3;
export const MAX_PAGE_SIZE = 50;

// margine di sicurezza (px): bordi, arrotondamenti delle misure
const SAFETY = 8;

/**
 * Quante righe stanno nello schermo.
 *
 * @param {object} m
 * @param {number} m.viewportHeight altezza della finestra
 * @param {number} m.aboveRows      spazio dall'inizio della pagina alla prima riga
 * @param {number} m.belowRows      spazio dall'ultima riga alla fine della pagina
 * @param {number} m.rowHeight      altezza media di una riga
 * @returns {number|null} null se le misure non bastano
 */
export function rowsThatFit({
  viewportHeight,
  aboveRows,
  belowRows,
  rowHeight,
  min = MIN_PAGE_SIZE,
  max = MAX_PAGE_SIZE,
}) {
  if (!(rowHeight > 0) || !(viewportHeight > 0)) return null;
  const free = viewportHeight - aboveRows - belowRows - SAFETY;
  const rows = Math.floor(free / rowHeight);
  return Math.min(max, Math.max(min, rows));
}

const px = (el, prop) => (el ? parseFloat(getComputedStyle(el)[prop]) || 0 : 0);

/**
 * Misura la tabella già disegnata e restituisce quante righe stanno nello
 * schermo, o null se non ci sono righe da misurare.
 */
export function measurePageSize(table) {
  if (!table || !table.tBodies || !table.tBodies[0]) return null;
  const rows = Array.from(table.tBodies[0].rows).filter(
    (r) => r.offsetParent !== null && !r.querySelector(".no-result")
  );
  if (rows.length === 0) return null;

  const first = rows[0].getBoundingClientRect();
  const last = rows[rows.length - 1].getBoundingClientRect();
  const rowHeight = (last.bottom - first.top) / rows.length;

  // contenitore della tabella (tab, pulsanti, tabella, paginazione)
  const wrap = table.closest(".tbl-wrap");
  const container = (wrap && wrap.parentElement) || table.parentElement;
  const main = table.closest("main");
  const footer = document.querySelector(".developed-container");

  const aboveRows = first.top + window.scrollY;
  const belowRows =
    container.getBoundingClientRect().bottom -
    last.bottom +
    px(container, "marginBottom") +
    px(main, "paddingBottom") +
    (footer ? footer.offsetHeight : 0);

  return rowsThatFit({
    viewportHeight: window.innerHeight,
    aboveRows,
    belowRows,
    rowHeight,
  });
}

/**
 * Mixin per le pagine con lista paginata. La pagina deve:
 * - avere ref="fitTable" sulla <table>
 * - usare this.pageSize come limit nelle chiamate
 * - chiamare this.fitPageSize() dopo il primo caricamento
 * - definire onPageSizeChange(): ricarica la lista (dalla pagina 1)
 * Si ricalcola anche quando cambia la dimensione della finestra.
 */
export const fitPageSizeMixin = {
  data() {
    return { pageSize: DEFAULT_PAGE_SIZE };
  },
  mounted() {
    window.addEventListener("resize", this.onFitResize);
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.onFitResize);
    clearTimeout(this.fitTimer);
  },
  methods: {
    onFitResize() {
      clearTimeout(this.fitTimer);
      this.fitTimer = setTimeout(() => this.fitPageSize(), 250);
    },
    async fitPageSize() {
      await this.$nextTick();
      const size = measurePageSize(this.$refs.fitTable);
      if (!size || size === this.pageSize) return;
      this.pageSize = size;
      await this.onPageSizeChange();
    },
  },
};
